import { createClient } from '@supabase/supabase-js';

/**
 * The browser's Supabase client.
 *
 * The anon key ships in the bundle and that is fine: it grants nothing on its
 * own, because RLS denies by default. Authorization comes from the user's JWT
 * and is enforced in the database (decision #11). The service-role key must
 * NEVER appear here — it bypasses RLS entirely and lives only in Edge Functions.
 */
function makeClient() {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    throw new Error(
      'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are required. Copy .env.example to .env.',
    );
  }

  return createClient(url, anonKey, {
    auth: {
      // The session is created by redeeming the token from auth-otp, never by a
      // redirect, so there is no token in the URL to detect.
      detectSessionInUrl: false,
      persistSession: true,
      autoRefreshToken: true,
      storageKey: 'radiology.session',
    },
    // `core` holds my_session and attributes — what the shell needs at boot.
    // Module data is reached with schema() below.
    db: { schema: 'core' },
  });
}

// Derived from createClient so the `core` default schema is preserved; the bare
// SupabaseClient type widens it back to `public`.
type Client = ReturnType<typeof makeClient>;

let client: Client | null = null;

export function supabase(): Client {
  client ??= makeClient();
  return client;
}

/** A client bound to one module schema, for that module's data. */
export function schema(name: 'certifications' | 'purchasing' | 'attendance' | 'devices') {
  return supabase().schema(name);
}

export function functionsUrl(path: string): string {
  const base = import.meta.env.VITE_SUPABASE_URL;
  return `${base}/functions/v1/${path.replace(/^\/+/, '')}`;
}
